import React, { useCallback, useReducer, useContext, useEffect } from "react";
import {
  Gender,
  IFamilyTreePerson,
  IFamilyTreeRelation,
} from "../../interfaces";
import { AddMemberModal } from "../../views/modals";
import { UserAddOutlined } from "@ant-design/icons";
import { Button, notification } from "antd";
import { Store } from "antd/lib/form/interface";
import { Backend } from "../../helpers";
import axios, { AxiosResponse, AxiosError, CancelTokenSource } from "axios";
import { authContext } from "../../providers";
import { IAuthContext } from "../../providers/AuthProvider";
import {
  SuggestionRelation,
  IFamilyTreeNodeSuggestion,
} from "../../interfaces/IFamilyTree";

export declare type AddMemberButtonProps = {
  suggetion: IFamilyTreeNodeSuggestion;
  gender: Gender;
  onSuccess: (person: IFamilyTreePerson) => void;
};

export type AddMemberButtonState = {
  modalVisible: boolean;
  createPersonTS: CancelTokenSource;
  createRelatedPerson: CancelTokenSource;
  appContext: IAuthContext;
};

const AddMemberButton: React.FC<AddMemberButtonProps> = (props) => {
  /**
   * States management
   */
  const reducer = (
    state: AddMemberButtonState,
    action: {
      type: "updateState" | "openModal" | "closeModal";
      state?: Partial<AddMemberButtonState>;
    }
  ): AddMemberButtonState => {
    switch (action.type) {
      case "updateState":
        return {
          modalVisible: action.state?.modalVisible ?? state.modalVisible,
          createPersonTS: action.state?.createPersonTS ?? state.createPersonTS,
          createRelatedPerson:
            action.state?.createRelatedPerson ?? state.createRelatedPerson,
          appContext: action.state?.appContext ?? state.appContext,
        };

      case "openModal": {
        const { modalVisible, ...rest } = state;
        return {
          ...rest,
          modalVisible: true,
        };
      }

      case "closeModal": {
        const { modalVisible, ...rest } = state;
        return {
          ...rest,
          modalVisible: false,
        };
      }

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    modalVisible: false,
    createPersonTS: axios.CancelToken.source(),
    createRelatedPerson: axios.CancelToken.source(),
    appContext: useContext(authContext),
  });

  useEffect(() => {
    return () => {
      state.createPersonTS.cancel();
      state.createRelatedPerson.cancel();
    };
  }, [state.createPersonTS, state.createRelatedPerson]);

  const getButtonLabel = () => {
    switch (props.suggetion.relationType) {
      case "child":
        if (props.gender === "male") {
          return "Add a son";
        } else if (props.gender === "female") {
          return "Add a daughter";
        } else {
          return "Add a child";
        }
      case "parent":
        if (props.gender === "male") {
          return "Add a father";
        } else if (props.gender === "female") {
          return "Add a mother";
        } else {
          return "Add a parent";
        }
      case "partner":
        return "Add a partner";
      case "sibling":
        if (props.gender === "male") {
          return "Add a brother";
        } else if (props.gender === "female") {
          return "Add a sister";
        } else {
          return "Add a sibling";
        }
      default:
        return "Add member";
    }
  };

  /**
   * Create a person
   */
  const createPerson = useCallback(
    async (values: Store) => {
      // start loading
      state.appContext.dispatch({ type: "startLoading" });
      // submit data
      await Backend.getInstance()
        .axios()
        .post(
          "/api/createnode/",
          {
            first_name: values.first_name,
            middle_name: values.middle_name,
            last_name: values.last_name,
            dob: values.dob.format("YYYY-MM-DD"),
            gender: values.gender,
            birthplace: values.birthplace,
          },
          {
            cancelToken: state.createPersonTS.token,
          }
        )
        .then((response: AxiosResponse<IFamilyTreePerson>) => {
          // notify user
          notification.success({
            message: `${[
              response.data.first_name,
              response.data.middle_name,
              response.data.last_name,
            ].join(" ")} has been successfully added in your tree!`,
          });
          // notify the parent component
          props.onSuccess(response.data);
        })
        .catch((error: AxiosError<any>) => {
          if (!axios.isCancel(error)) {
            notification.error({
              message:
                "Failed to add the family member. Try again later please.",
            });
          }
        })
        .finally(() => {
          // stop loading
          state.appContext.dispatch({ type: "stopLoading" });
        });
    },
    [props, state.appContext, state.createPersonTS]
  );

  /**
   * Create a person
   */
  const createRelatedPerson = useCallback(
    async (values: Store, relations: SuggestionRelation[]) => {
      /**
       * Experimenal code
       */
      if (relations.length > 0) {
        // start loading
        state.appContext.dispatch({ type: "startLoading" });
        // submit data
        await Backend.getInstance()
          .axios()
          .post(
            "/api/createnode/",
            {
              first_name: values.first_name,
              middle_name: values.middle_name,
              last_name: values.last_name,
              dob: values.dob.format("YYYY-MM-DD"),
              gender: values.gender,
              birthplace: values.birthplace,
            },
            {
              cancelToken: state.createPersonTS.token,
            }
          )
          .then(async (response: AxiosResponse<IFamilyTreePerson>) => {
            // created person
            const person = response.data;

            // notify user
            notification.success({
              message: `${[
                person.first_name,
                person.middle_name,
                person.last_name,
              ].join(" ")} has been successfully added in your tree!`,
            });

            /**
             * save relations
             */
            for (let index = 0; index < relations.length; index++) {
              const relation = relations[index];

              // submit relation
              await Backend.getInstance()
                .axios()
                .post("/api/createrelation/", {
                  relation: relation.relation,
                  relation_from:
                    relation.relationType === "from"
                      ? relation.relationWith
                      : person.id,
                  relation_to:
                    relation.relationType === "to"
                      ? relation.relationWith
                      : person.id,
                })
                .then(
                  (relationResponse: AxiosResponse<IFamilyTreeRelation>) => {
                    // notify user
                    notification.success({
                      message: `${relation.relation} relation has been successfully created!`,
                    });
                  }
                )
                .catch((relationError: AxiosError<any>) => {
                  // notify user
                  notification.error({
                    message: `Failed to create ${relation.relation} relation. Try again later please.`,
                  });
                });
            }

            // notify the parent component
            props.onSuccess(response.data);
          })
          .catch((error: AxiosError<any>) => {
            if (!axios.isCancel(error)) {
              notification.error({
                message:
                  "Failed to add the family member. Try again later please.",
              });
            }
          })
          .finally(() => {
            // stop loading
            state.appContext.dispatch({ type: "stopLoading" });
          });
      }
    },
    [props, state.appContext, state.createPersonTS]
  );

  const label = getButtonLabel();

  return (
    <>
      <Button
        icon={<UserAddOutlined />}
        onClick={() => {
          dispatch({ type: "openModal" });
        }}
        onTouchEnd={() => {
          dispatch({ type: "openModal" });
        }}
        className="rounded-md my-4 flex items-center"
      >
        {label}
      </Button>
      <AddMemberModal
        title={label}
        suggestion={{
          gender: props.gender,
        }}
        visible={state.modalVisible}
        handleSubmit={async (values: Store) => {
          console.log("Form submitted", values);
          dispatch({ type: "closeModal" });

          if (props.suggetion.relationType === "unknown") {
            await createPerson(values);
          } else {
            await createRelatedPerson(values, props.suggetion.personInvolved);
          }
        }}
        onCancel={() => {
          dispatch({ type: "closeModal" });
        }}
      />
    </>
  );
};

export default AddMemberButton;
